import throttle from 'lodash/throttle';

// Core modules that are needed on every page
import { initializeAOS } from './modules/aos.js';
import { initializeNavbar } from './modules/navbar.js';
import { initializeDropdowns } from './modules/dropdowns.js';
import { initializeStickyButtons } from './modules/sticky-buttons.js';
import { initializeBarba } from './modules/barba.js';
import { initializeMegaMenu } from './modules/mega-menu.js';
import { initializeUrlAppender } from './modules/url-appender.js';
import { initializeHandleUmbracoFormsAjax } from './modules/handle-umbraco-forms-ajax.js';
import { initializeFormPopups } from './modules/form-popups.js';

// Dynamic imports for page-specific modules
const loadPageSpecificModules = async () => {
  try {
    // Check if elements exist before loading modules
    const hasSwiper = document.querySelector('.swiper') || document.querySelector('[class*="swiper-"]');
    const hasLatestIssues = document.querySelector('.widget-latest-issues') || document.querySelector('[class*="latest-issues"]');
    const hasEvents = document.querySelector('.widget-events') || document.querySelector('[class*="events"]');
    const hasTeamMembers = document.querySelector('.widget-team-members') || document.querySelector('[class*="team-members"]');
    const hasExperts = document.querySelector('.widget-experts') || document.querySelector('[class*="experts"]');
    const hasCooperatives = document.querySelector('.widget-cooperatives') || document.querySelector('[class*="cooperatives"]');
    const hasComplaints = document.querySelector('.widget-consumer-complaints') || document.querySelector('[class*="consumer-complaints"]');
    const hasAgencies = document.querySelector('.widget-agencies') || document.querySelector('[class*="agencies"]');
    const hasNewsroom = document.querySelector('.widget-newsroom') || document.querySelector('[class*="newsroom"]');
    const hasLatestNews = document.querySelector('.widget-latest-news') || document.querySelector('[class*="latest-news"]');
    const hasSearchResults = document.querySelector('.search-results') || document.querySelector('[class*="search"]');
    const hasDonationWidget = document.querySelector('.widget-donation') || document.querySelector('[class*="donation"]');
    const hasImageVideoGallery = document.querySelector('.widget-image-video-gallery');
    const hasTypeaheadElements = document.querySelector('[data-typeahead="true"]');
    const hasFormModals = document.querySelector('.modal-form-init');
    const hasDualCards = document.querySelector('.widget-dual-info-cards');

    const modulePromises = [];

    // Always load these modules as they might be used across pages
    modulePromises.push(
      import('./modules/featured-resources.js').then(module => module.initializeFeaturedResources()),
      import('./modules/ribbon.js').then(module => module.initializeRibbon()),
      import('./modules/video-modals.js').then(module => module.initializeVideoThumbnails()),
      import('./modules/customer-location.js').then(module => module.initializeLocationDropdown()),
      import('./modules/sticky-nav.js').then(module => module.initializeStickyNav()),
      import('./modules/contacts-widget.js').then(module => module.initializeContactsWidget()),
      import('./modules/media-kits.js').then(module => module.initializeMediaKits()),
      import('./modules/social-share.js').then(module => module.initializeSocialShare()),
      import('./modules/file-fields.js').then(module => module.initializeFileFields())
    );

    // Load modules based on page content
    if (hasSwiper) {
      modulePromises.push(import('./modules/swiper.js').then(module => module.initializeSwipers()));
    }

    if (hasLatestIssues) {
      modulePromises.push(
        import('./modules/latest-issues.js').then(module => {
          module.initializeLatestIssues();
        })
      );
    }

    if (hasNewsroom) {
      modulePromises.push(import('./modules/newsroom.js').then(module => module.initializeNewsroom()));
    }

    if (hasEvents) {
      modulePromises.push(
        import('./modules/events-carousel.js').then(module => module.initializeEventsCarousel()),
        import('./modules/events-list.js').then(module => module.initializeEventsList()),
        import('./modules/upcoming-events.js').then(module => module.initializeUpcomingEvents())
      );
    }

    if (hasTeamMembers) {
      modulePromises.push(import('./modules/team-members.js').then(module => module.initializeTeamMembers()));
    }

    if (hasExperts) {
      modulePromises.push(import('./modules/experts-cards.js').then(module => module.initializeExpertCards()));
    }

    if (hasCooperatives) {
      modulePromises.push(
        import('./modules/cooperatives-listing.js').then(module => module.initializeCooperativesListing()),
        import('./modules/consumer-cooperatives-filter.js').then(module => module.initializeConsumerCooperativesFilter())
      );
    }

    if (hasComplaints) {
      modulePromises.push(import('./modules/consumer-complaints.js').then(module => module.initializeConsumerComplaints()));
    }

    if (hasAgencies) {
      modulePromises.push(import('./modules/agencies-list.js').then(module => module.initializeAgenciesList()));
    }

    if (hasLatestNews) {
      modulePromises.push(import('./modules/latest-news-slider-filter.js').then(module => module.initializeLatestNewsSliderFilter()));
    }

    if (hasSearchResults) {
      modulePromises.push(
        import('./modules/search-results.js').then(module => module.initializeSearchResults()),
        import('./modules/search-information.js').then(module => module.initializeSearchInformation())
      );
    }

    if (hasDonationWidget) {
      modulePromises.push(import('./modules/donation-widget.js').then(module => module.initializeDonationWidget()));
    }

    if (hasImageVideoGallery) {
      modulePromises.push(import('./modules/image-video-gallery.js').then(module => module.initializeImageVideoGallery()));
    }

    if (hasTypeaheadElements) {
      modulePromises.push(import('./modules/typeahead.js').then(module => module.initializeTypeahead()));
    }

    if (hasDualCards) {
      modulePromises.push(import('./modules/dual-cards.js').then(module => module.initializeDualCards()));
    }

    // Wait for all modules to load and initialize
    await Promise.all(modulePromises);
  } catch (error) {
    console.error('Application: Failed to load page-specific modules', error);
  }
};

/**
 * Initialize all components
 * @returns {void}
 */
export const initializeComponents = async () => {
  try {
    // Initialize core components first
    initializeAOS();
    initializeStickyButtons();
    initializeNavbar();
    initializeDropdowns();
    initializeMegaMenu();
    initializeUrlAppender();
    initializeHandleUmbracoFormsAjax();
    initializeFormPopups();
    // Load page-specific modules
    await loadPageSpecificModules();
  } catch (error) {
    console.error('Application: Failed to initialize components', error);
  }
};

/**
 * Adjusts main offset based on header height
 * @returns {void}
 */
export const adjustMainOffsetForHeader = async () => {
  try {
    const header = document.querySelector('header');
    const main = document.querySelector('main');

    if (header && main) {
      main.style.paddingTop = `${header.offsetHeight}px`;
    }
  } catch (error) {
    console.error('Application: Failed to initialize components', error);
  }
};

/**
 * Handle scroll events for navbar
 * @returns {void}
 */
const handleScroll = () => {
  try {
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      navbar.classList.toggle('navbar--scrolled', scrollTop > 0);
    }
  } catch (error) {
    console.error('Application: Error handling scroll', error);
  }
};

// Initialize Barba.js first
// Initialize components when DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  adjustMainOffsetForHeader();
  await initializeComponents();
  initializeBarba();

});
// window.addEventListener('load', async () => {
//   adjustMainOffsetForHeader();
//   await initializeComponents();
//   initializeBarba();

// });

// Throttle scroll event for better performance
window.addEventListener('scroll', throttle(handleScroll, 100));
